import request from "axios";
import { getNodeEnv } from "domain/helpers/getNodeEnv";
import { apiBase } from "../../constants";

// A single error can fire in a loop (e.g. a rejection on every re-render), and
// one open tab then writes hundreds of thousands of exception rows an hour.
// Send at most this many logs of the same error per window...
const MAX_LOGS_PER_ERROR = 5;
// ...and at most this many in total, for errors whose text differs every time.
const MAX_LOGS = 60;
const LOG_WINDOW_MS = 60 * 1000;
// Distinct errors tracked at once, so varying messages can't grow the map
// without bound
const MAX_TRACKED_ERRORS = 200;

type LogWindow = { count: number; windowStart: number };
const logsPerError = new Map<string, LogWindow>();
const allLogs: LogWindow = { count: 0, windowStart: 0 };

// Promises can be rejected with anything ({ status: 404 }, undefined, ...);
// tell those apart by their content rather than lumping them all together.
const describeValue = (value: unknown) => {
  try {
    return (JSON.stringify(value) ?? String(value)).slice(0, 200);
  } catch (e) {
    return String(value);
  }
};

// Callers send a constant message ("Frontend Unhandled Rejection", ...), so
// tell errors apart by what was thrown, or one looping error would also
// silence every other error from the tab.
const getErrorKey = (payload: any) => {
  const extra = payload?.extra;
  const thrown = extra?.reason ?? extra?.error;
  const thrownMessage =
    typeof thrown === "string"
      ? thrown
      : thrown?.message ?? describeValue(thrown);
  return [payload?.message, thrown?.name, thrownMessage, extra?.message]
    .map((part) => String(part ?? ""))
    .join("|");
};

const forgetOldErrors = (now: number) => {
  logsPerError.forEach((logWindow, key) => {
    if (now - logWindow.windowStart > LOG_WINDOW_MS) {
      logsPerError.delete(key);
    }
  });
  // Still full within one window: drop the oldest (Maps keep insertion order)
  const keys = logsPerError.keys();
  while (logsPerError.size >= MAX_TRACKED_ERRORS) {
    logsPerError.delete(keys.next().value as string);
  }
};

const countLog = (logWindow: LogWindow, limit: number, now: number) => {
  if (now - logWindow.windowStart > LOG_WINDOW_MS) {
    logWindow.count = 0;
    logWindow.windowStart = now;
  }
  logWindow.count += 1;
  return logWindow.count <= limit;
};

const isThrottled = (payload: any) => {
  const now = Date.now();
  const key = getErrorKey(payload);
  let errorLogs = logsPerError.get(key);
  if (!errorLogs) {
    if (logsPerError.size >= MAX_TRACKED_ERRORS) {
      forgetOldErrors(now);
    }
    errorLogs = { count: 0, windowStart: now };
    logsPerError.set(key, errorLogs);
  }
  if (!countLog(errorLogs, MAX_LOGS_PER_ERROR, now)) {
    return true;
  }
  return !countLog(allLogs, MAX_LOGS, now);
};

export const log = async (payload: any) => {
  // Only log to backend in production,
  // since we have the error overlay in dev.
  const env = getNodeEnv();
  if (env === "production" || env === "staging") {
    if (isThrottled(payload)) {
      return;
    }
    try {
      await request({
        url: `${apiBase}/v1/logging`,
        method: "POST",
        data: payload,
        withCredentials: true,
        validateStatus: (status) => status < 300,
      });
    } catch (e) {
      console.log("Couldn't log error to backend.");
    }
  }
};
